import { useQuery } from "@tanstack/react-query";
import $api from "@/zapi-frontend/shared/api/axios";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useEffect, useRef } from "react";
import { TUserRole } from "@/zapi-frontend/pages/SettingsPage/services/schemas";
import { syncPostHogAfterConsent } from "@/zapi-frontend/app/providers/analytics";
import { useCookieConsentStore } from "@/zapi-frontend/shared/hooks/useCookieConsent";

export type User = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: number;
  organizationName: string | null;
  uiLanguage: string | null;
  featurePermissionLevel: number;
  role: TUserRole;
  customSnippetsUrl: string | null;
  profileImageUrl: string | null;
  organizationLogoUrl: string | null;
  passwordExpiringSoon?: boolean;
  passwordExpiryDate?: string;
  pinnedPromptRevisions: TPinnedPromptRevision[];
};

export type TPinnedPromptRevision = {
  featureKey: string;
  revisionId: number;
  revisionNumber: number | null;
};

export const useAuthenticated = () => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["session-verify"],
    queryFn: async (): Promise<User | null> => {
      try {
        const response = await $api.post("/auth/verify");
        return response?.data?.data as User;
      } catch {
        return null;
      }
    }
  });

  const { setMe } = useMeStore();
  const consentSyncedForUser = useRef<number | null>(null);

  useEffect(() => {
    if (data) {
      setMe({
        ...data,
        isLabs: data.featurePermissionLevel === 1,
        customSnippetsUrl: data.customSnippetsUrl,
        pinnedPromptRevisions: data.pinnedPromptRevisions ?? [],
        refetch: async () => {
          await refetch();
        }
      });

      if (consentSyncedForUser.current !== data.id) {
        consentSyncedForUser.current = data.id;
        const store = useCookieConsentStore.getState();
        store.setUserId(data.id).then(() => {
          syncPostHogAfterConsent();
          if (!useCookieConsentStore.getState().hasConsented) {
            useCookieConsentStore.getState().openModal();
          }
        });
      }
    }
  }, [data, setMe, refetch]);

  return { data: !!data?.email, isLoading, refetch, me: data };
};
